import { useContext } from "react";
import { ActionKey, ObjectType } from "common/utils/actionBar";
import { getUserErrors } from "common-client/utils/apollo";
import { useCreateBulkOperationMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { TableRowsSelectionState } from "../Tables/RowSelectionProvider";
import { getTableNameForTracking, pluralizeObjectType } from "./utils";

type VisibilityOperation = "hide" | "show";

export function useActionBarVisibilityHandlers({
  state,
  objectType,
  onComplete,
  setLoadingAction,
}: {
  state: TableRowsSelectionState;
  objectType: ObjectType;
  onComplete: () => void;
  setLoadingAction: (loadingAction: ActionKey | null) => void;
}) {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { totalSelected } = state;
  const pluralizedObjectType = pluralizeObjectType(objectType, totalSelected);

  const [createBulkOperation] = useCreateBulkOperationMutation({
    onCompleted: (data, clientOptions) => {
      const operation = clientOptions?.variables?.data
        .operation as VisibilityOperation;
      if (data.createBulkOperation.success) {
        const { message, processedCount } = data.createBulkOperation;
        if (processedCount === 0) {
          addSuccessToast("No files had their visibility changed");
        } else {
          const actionText = operation === "hide" ? "hidden from" : "shown to";
          addSuccessToast(
            message ??
              `Successfully ${actionText} public: ${processedCount} ${pluralizedObjectType}`,
          );
        }
      }
    },
    onError: (error, clientOptions) => {
      const operation = clientOptions?.variables?.data
        .operation as VisibilityOperation;
      const userError = getUserErrors(error);
      const actionText = operation === "hide" ? "hiding" : "showing";
      if (userError) {
        addErrorToast(userError);
      } else {
        addErrorToast(
          `An error occurred while ${actionText} ${pluralizedObjectType}. Please try again.`,
        );
      }
    },
  });

  const handleVisibilityChange = async (operation: VisibilityOperation) => {
    const tableName = getTableNameForTracking(state.queryDescription!);

    track("Bulk action triggered", {
      operation,
      tableName,
      totalSelected,
    });

    setLoadingAction("visibility");

    await createBulkOperation({
      variables: {
        data: {
          objectType,
          operation,
          selectorParameters: {
            accountId: account?.id ?? "",
            mode: state.mode,
            ids: state.ids,
            queryDescription: JSON.stringify(state.queryDescription),
            totalCount: state.totalSelected,
          },
        },
      },
    });

    setLoadingAction(null);
    onComplete();
  };

  return {
    handleShowToPublic: () => handleVisibilityChange("show"),
    handleHideFromPublic: () => handleVisibilityChange("hide"),
  };
}
